import { useState } from "react";

import { Button, Checkbox, Input, Label } from "@bigbinary/neeto-atoms";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
} from "@bigbinary/neeto-atoms/primitives";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import i18next from "i18next";
import { Search } from "lucide-react";
import { isNotEmpty, findIndexBy, noop } from "neetocist";
import PropTypes from "prop-types";
import { assoc, isEmpty, pluck, includes } from "ramda";
import { useTranslation } from "react-i18next";

import Item from "./Item";
import useTableConfigurations from "./useTableConfigurations.js";
import { filterBySearchTerm, getHidableColumns } from "./utils.js";

const Columns = ({
  actionBlock,
  checkboxProps = {},
  columnData = [],
  isSearchable = false,
  tableKey = "",
  noColumnMessage = i18next.t("neetoMolecules.columns.noColumns"),
  searchProps = {},
  allowConfigurationResetOnUpdate = true,
  onMutationSuccess = noop,
  defaultHiddenColumns = [],
  buttonProps = {},
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const {
    allProcessedColumns,
    existingColumnConfiguration,
    hideColumn,
    showColumn,
    updateColumns,
    destroyConfiguration,
    isTableConfigurationFetching,
  } = useTableConfigurations(
    tableKey,
    columnData,
    onMutationSuccess,
    allowConfigurationResetOnUpdate,
    defaultHiddenColumns
  );

  const hiddenColumns = pluck(
    "key",
    existingColumnConfiguration.filter(column => column.isShown === false)
  );

  const handleDragEnd = event => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = findIndexBy(
      { key: active.id },
      existingColumnConfiguration
    );

    const newIndex = findIndexBy({ key: over.id }, existingColumnConfiguration);

    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = [...existingColumnConfiguration];
    const [movedItem] = newOrder.splice(oldIndex, 1);
    newOrder.splice(newIndex, 0, movedItem);
    updateColumns(newOrder);
  };

  const columns = getHidableColumns(allProcessedColumns);
  const filteredColumns = filterBySearchTerm(searchTerm, columns);

  const handleHideAllColumns = () => {
    const columnsToHideKeys = pluck("key", columns);

    updateColumns(
      existingColumnConfiguration.map(column =>
        includes(column.key, columnsToHideKeys)
          ? assoc("isShown", false, column)
          : column
      )
    );
  };

  const handleChange = ({ target: { name: dataIndex, checked } }) => {
    if (checked) showColumn(dataIndex);
    else hideColumn(dataIndex);
  };

  const handleSearch = ({ target: { value } }) => setSearchTerm(value);

  const {
    icon: Icon,
    "data-testid": dataTestId = "columns-dropdown-button",
    label: buttonLabel,
    ...restButtonProps
  } = buttonProps;

  return (
    <Popover
      {...{ open }}
      onOpenChange={isOpen => {
        setOpen(isOpen);
        if (!isOpen) setSearchTerm("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          data-testid={dataTestId}
          icon={Icon || undefined}
          label={buttonLabel || undefined}
          size={buttonLabel ? "default" : "icon"}
          variant="ghost"
          {...restButtonProps}
        />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="max-h-[calc(100dvh-200px)] w-80 max-w-[31.25rem] overflow-y-auto p-0"
      >
        <div data-testid="customize-columns-dropdown-container">
          {isSearchable && (
            <div className="relative p-3">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-6 size-4 -translate-y-1/2" />
              <Input
                className="pl-9"
                data-testid="neeto-ui-columns-search"
                placeholder={t("neetoMolecules.columns.search")}
                type="search"
                value={searchTerm}
                onChange={handleSearch}
                {...searchProps}
              />
            </div>
          )}
          {isEmpty(filteredColumns) && (
            <span className="flex flex-col items-center p-2">
              {noColumnMessage}
            </span>
          )}
          {isNotEmpty(filteredColumns) && !searchTerm && (
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={pluck("key", filteredColumns)}
                strategy={verticalListSortingStrategy}
              >
                {filteredColumns.map(column => (
                  <Item
                    {...{
                      checkboxProps,
                      column,
                      hiddenColumns,
                      isTableConfigurationFetching,
                    }}
                    key={column.key}
                    onToggle={handleChange}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
          {isNotEmpty(filteredColumns) &&
            searchTerm &&
            filteredColumns.map(({ key, title }) => (
              <Label
                className="hover:bg-accent flex w-full cursor-pointer items-center px-5 py-2"
                htmlFor={key}
                key={key}
              >
                <Checkbox
                  checked={!hiddenColumns.includes(key)}
                  data-testid="neeto-ui-columns-checkbox"
                  id={key}
                  name={key}
                  onCheckedChange={checked => {
                    handleChange({ target: { name: key, checked } });
                  }}
                  {...checkboxProps}
                />
                <span className="ml-2 text-sm">{title}</span>
              </Label>
            ))}
          <Separator />
          {!!actionBlock && (
            <>
              {actionBlock}
              <Separator />
            </>
          )}
          <div className="flex justify-end gap-2 px-3 py-2">
            <Button
              data-testid="column-visibility-hide-all-button"
              size="sm"
              variant="ghost"
              onClick={handleHideAllColumns}
            >
              {t("neetoMolecules.columns.hideAll")}
            </Button>
            <Button
              data-testid="column-visibility-reset-button"
              size="sm"
              variant="outline"
              onClick={() => destroyConfiguration(tableKey)}
            >
              {t("neetoMolecules.columns.reset")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

Columns.propTypes = {
  allowConfigurationResetOnUpdate: PropTypes.bool,
  actionBlock: PropTypes.element,
  checkboxProps: PropTypes.object,
  columnData: PropTypes.array,
  isSearchable: PropTypes.bool,
  tableKey: PropTypes.string,
  noColumnMessage: PropTypes.string,
  searchProps: PropTypes.object,
  onMutationSuccess: PropTypes.func,
};

Columns.displayName = "Columns";
export default Columns;
